import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
    exactChip: {
        backgroundColor: '#00000014', //  '#d0f0c0', // light green
        color: '#000000de', //'#1b5e20', // dark green
    },
    similarChip: {
        backgroundColor: '#fff4e6', // soft orange
        color: '#e65100', // deep orange
    },
    missingChip: {
        backgroundColor: '#fce4ec', // soft pink
        color: '#ad1457', // deep rose
    },
    extraChip: {
        backgroundColor: '#ede7f6', // soft lavender
        color: '#4527a0', // deep purple
    },
});

function OrderSummaryComparision(props) {
    const { itemMappingDetails, orderSummaryByCrowd, orderSummaryByAI, error } =
        props;

    const classes = useStyles();

    const renderChipContent = (itemId, type, label) => {
        let item = {};
        if (type === 'crowd') {
            item = orderSummaryByCrowd.find((x) => x.id === itemId);
        } else {
            item = orderSummaryByAI.find((x) => x.id === itemId);
        }

        return (
            <div
                style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    width: '100%',
                    textAlign: 'center',
                }}
            >
                <div
                    style={{
                        marginBottom: '5px',
                        fontStyle: 'italic',
                        fontSize: ' 0.9em',
                    }}
                >
                    {`<${label} item>`}
                </div>
                {/* <div> */}
                <b>{`${item.quantity} x ${item.name}`}</b>
                {item.choice && (
                    <div
                        style={{
                            marginTop: '2px',
                            fontStyle: 'italic',
                            fontSize: ' 0.9em',
                        }}
                    >
                        {item.choice}
                    </div>
                )}
                {item.modifiersStr && (
                    <div
                        style={{
                            marginTop: '2px',
                            fontStyle: 'italic',
                            fontSize: ' 0.9em',
                        }}
                    >
                        {item.modifiersStr}
                    </div>
                )}
                {item.notes && (
                    <div
                        style={{
                            marginTop: '2px',
                            fontStyle: 'italic',
                            fontSize: ' 0.9em',
                        }}
                    >
                        {item.notes}
                    </div>
                )}
                {/* </div> */}
            </div>
        );
    };

    const renderItemAsChip = (itemId, dataOriginType, comparisionType) => {
        let styleType = '';
        let label = '';

        switch (comparisionType) {
            case 'missing':
                styleType = classes.missingChip;
                label = 'missing';
                break;
            case 'extra':
                styleType = classes.extraChip;
                label = 'extra';
                break;
            case 'similar':
                styleType = classes.similarChip;
                label = 'similar';
                break;
            case 'exact':
                styleType = classes.exactChip;
                label = 'exact';
                break;

            default:
                styleType = '';
                label = '';
        }

        return (
            <Chip
                className={styleType}
                size="small"
                sx={{
                    padding: '5px 10px',
                    height: 'auto',
                    '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        display: 'block',
                        lineHeight: 1.2,
                        width: '100%',
                        textAlign: 'center',
                        padding: '4px 8px',
                    },
                }}
                label={renderChipContent(itemId, dataOriginType, label)}
            />
        );
    };

    //if there is an error then display the error
    if (error)
        return (
            <Typography
                variant="h6"
                sx={{
                    color: '#e65100',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                }}
            >
                {error}
            </Typography>
        );

    //if the itemMappingDetails data is not present, then don't show anything
    if (!itemMappingDetails || Object.keys(itemMappingDetails).length === 0)
        return null;

    return (
        <Grid
            container
            direction={'row'}
            spacing={2}
            sx={{ maxHeight: '99%', overflowY: 'auto', maxWidth: '99%' }}
        >
            {/* <Grid item xs={12} textAlign="center">
                <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
                    UnMatched items
                </Typography>
            </Grid> */}
            {(itemMappingDetails.extraItems.length > 0 ||
                itemMappingDetails.missingItems.length > 0) && (
                <Grid item container columnSpacing={3}>
                    <Grid
                        item
                        container
                        xs={6}
                        direction={'column'}
                        rowSpacing={1}
                        justifyContent={'flex-start'}
                        alignItems={'center'}
                    >
                        {itemMappingDetails.missingItems.map((x) => (
                            <Grid item key={x}>
                                {renderItemAsChip(x, 'crowd', 'missing')}
                            </Grid>
                        ))}
                    </Grid>
                    <Grid
                        item
                        container
                        xs={6}
                        direction={'column'}
                        rowSpacing={1}
                        justifyContent={'flex-start'}
                        alignItems={'center'}
                    >
                        {itemMappingDetails.extraItems.map((x) => (
                            <Grid item key={x}>
                                {renderItemAsChip(x, 'AI', 'extra')}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            )}

            {itemMappingDetails.similarItems.length > 0 && (
                <Grid item container columnSpacing={3}>
                    <Grid
                        item
                        container
                        xs={6}
                        direction={'column'}
                        rowSpacing={1}
                        justifyContent={'flex-start'}
                        alignItems={'center'}
                    >
                        {itemMappingDetails.similarItems.map((x) => (
                            <Grid item key={x}>
                                {renderItemAsChip(x, 'crowd', 'similar')}
                            </Grid>
                        ))}
                    </Grid>
                    <Grid
                        item
                        container
                        xs={6}
                        direction={'column'}
                        rowSpacing={1}
                        justifyContent={'flex-start'}
                        alignItems={'center'}
                    >
                        {itemMappingDetails.similarItems.map((x) => (
                            <Grid item key={x}>
                                {renderItemAsChip(x, 'AI', 'similar')}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            )}

            {/* <Grid item xs={12} textAlign="center">
                <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
                    Matched items
                </Typography>
            </Grid> */}

            {itemMappingDetails.exactItems.length > 0 && (
                <Grid item container columnSpacing={3}>
                    <Grid
                        item
                        container
                        xs={6}
                        direction={'column'}
                        rowSpacing={1}
                        justifyContent={'flex-start'}
                        alignItems={'center'}
                    >
                        {itemMappingDetails.exactItems.map((x) => (
                            <Grid item key={x}>
                                {renderItemAsChip(x, 'crowd', 'exact')}
                            </Grid>
                        ))}
                    </Grid>
                    <Grid
                        item
                        container
                        xs={6}
                        direction={'column'}
                        rowSpacing={1}
                        justifyContent={'flex-start'}
                        alignItems={'center'}
                    >
                        {itemMappingDetails.exactItems.map((x) => (
                            <Grid item key={x}>
                                {renderItemAsChip(x, 'AI', 'exact')}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}

OrderSummaryComparision.propTypes = {
    itemMappingDetails: PropTypes.shape({
        exactItems: PropTypes.array,
        similarItems: PropTypes.array,
        extraItems: PropTypes.array,
        missingItems: PropTypes.array,
    }),
    orderSummaryByCrowd: PropTypes.arrayOf(PropTypes.shape({})),
    orderSummaryByAI: PropTypes.arrayOf(PropTypes.shape({})),
};

export default OrderSummaryComparision;
